/**
 * The signed-in shell: it holds the socket open and shows the banner.
 *
 * ┌─ ⛔ WHAT IT IS, AND WHAT IT STILL IS NOT ───────────────────────────────────┐
 * │ It holds the socket open, shows the disconnected banner, and renders the    │
 * │ conversation list. **Still NOT a thread, a router, or a layout** - those    │
 * │ are scope items 9 to 12.                                                    │
 * │                                                                            │
 * │ ⚠️ THE LIST ARRIVED IN A SEPARATE STEP AND THIS BLOCK USED TO FORBID IT.    │
 * │ It said "NOT a conversation list", because building one to have somewhere   │
 * │ to put a socket would have been scope creep - the list has a mandated       │
 * │ query shape and a plan guard waiting for it, and arriving early would have  │
 * │ meant arriving ahead of its own measurements.                               │
 * │                                                                            │
 * │ **The order held: the query was measured and registered with the plan       │
 * │ guard before anything rendered it.** The sentence is rewritten rather than  │
 * │ deleted, because a comment that forbids what the file now does is the stale │
 * │ argument corrected in `broadcast.ts`, and a reader who remembers the rule   │
 * │ should see that it was satisfied rather than dropped.                       │
 * └────────────────────────────────────────────────────────────────────────────┘
 *
 * ── ⛔ WHY IT EXISTS AT ALL, GIVEN IT RENDERS ALMOST NOTHING ─────────────────
 *
 * Before this file, `startInboxSocket`, `query-store` and `presentConnection`
 * were **three modules with no caller**. Everything proven about them was proven
 * about code nothing ran.
 *
 * That is the shape this project has recorded at its largest: the inbound
 * broadcast was specified in SIX places and implemented in none, and the phase's
 * own definition of complete depended on it. A module with no caller is not
 * "ready", it is unexercised.
 *
 * ── THE SOCKET'S LIFETIME IS THE SIGNED-IN SESSION'S ────────────────────────
 *
 * Mounted inside the signed-in branch, so signing out unmounts it and `stop()`
 * runs. ⚠️ THAT MATTERS MORE THAN IT LOOKS: without it the socket keeps
 * reconnecting after logout, each attempt carrying a cookie the server has
 * already destroyed, producing a 401 loop that reads as an auth defect.
 */
import { useEffect, useState } from 'react'
import {
  type ConnectionState,
  type InboxSocket,
  startInboxSocket,
} from '../data/inbox-socket.js'
import { presentConnection } from '../data/connection-presentation.js'
import { ConversationList } from './ConversationList.js'
import { ThreadView } from './ThreadView.js'
import { ThreadHeader } from './ThreadHeader.js'
import { CommandPalette } from './CommandPalette.js'
import { shortcutHintFor } from './palette-view.js'
import { RightPanel } from './RightPanel.js'
import { Composer } from './Composer.js'
import { useConversations } from './useConversations.js'
import { useThread } from './useThread.js'
import { usePanel } from './usePanel.js'
import { deleteNoteApi, editNoteApi, pinNoteApi, postComposed } from '../data/inbox-api.js'
import { refetchAll } from '../data/query-store.js'

/**
 * ⚠️ THE GLOBAL IS READ HERE AND NOWHERE ELSE, so `shortcutHintFor` stays pure
 * and both of its branches are reachable in a test. `navigator` is absent in a
 * non-browser environment, hence the guard rather than a direct read.
 */
const shortcutHint = (): string =>
  shortcutHintFor(typeof navigator === 'undefined' ? '' : navigator.platform)

export type InboxShellProps = {
  /**
   * TEST SEAM. jsdom has no server to talk to, so the component test injects a
   * fake. Production passes nothing.
   *
   * ⚠️ It replaces the SOCKET, never the PRESENTATION. What the banner says for
   * a given state is decided by `presentConnection` and asserted exhaustively in
   * `tests/connection-presentation.test.ts`; substituting that here would let a
   * component test agree with itself. See `tests/web/README.md` for which test
   * owns which property.
   */
  start?: typeof startInboxSocket
  /**
   * Which conversation is open, or `null` for none.
   *
   * ⛔ IT COMES FROM THE URL AND NOT FROM STATE HERE, which is the whole point
   * of wiring a route at all. Held as component state, an open thread would
   * survive nothing: no deep link, no refresh, no Back, and no returning to
   * where you were after signing in again. `App.tsx` reads it; this renders it.
   */
  conversationId?: string | null
  /**
   * ⛔ THE MESSAGE A SEARCH RESULT ARRIVED AT. `Build_Order:490` requires the
   * thread to open SCROLLED TO it and highlighted, so it travels from the URL
   * rather than being component state - a deep link that scrolled only the
   * first time would be a link that behaves differently on a refresh.
   */
  highlightMessageId?: string | null
  /**
   * The signed-in user. **Used for exactly one thing:** deciding whether a note
   * shows an Edit control, because only its author may edit it. Not a general
   * "who am I" channel - every other surface that needs the user has it already.
   */
  currentUserId?: string | null
  /** Opening a conversation is a NAVIGATION. `App.tsx` owns both directions. */
  onOpen?: (id: string) => void
}

export function InboxShell({
  start = startInboxSocket,
  conversationId = null,
  highlightMessageId = null,
  onOpen,
  currentUserId = null,
}: InboxShellProps): React.JSX.Element {
  const [state, setState] = useState<ConnectionState>({ kind: 'idle' })
  const [paletteOpen, setPaletteOpen] = useState(false)
  /**
   * ⛔ WHICH HEADER MENU THE PALETTE ASKED FOR, and it is a REQUEST rather than
   * state the header owns. The header opens that menu and clears this, so the
   * palette never becomes a second place that knows how snooze or assign work.
   */
  const [headerMenu, setHeaderMenu] = useState<'snooze' | 'assign' | null>(null)
  const { rows, error, loadMore, more } = useConversations()
  const {
    rows: threadRows,
    pinned: threadPinned,
    error: threadError,
    loadMore: loadMoreThread,
    more: threadMore,
  } = useThread(conversationId)
  const { panel, error: panelError } = usePanel(conversationId)

  useEffect(() => {
    let socket: InboxSocket | null = null
    // ⚠️ `onState` is passed at construction rather than polled. A poll would
    // have an interval nobody can choose, and the state changes are events.
    socket = start({ onState: setState })
    return () => {
      socket?.stop()
    }
    // ⛔ EMPTY DEPS ON PURPOSE: one socket per mount. Including `start` would
    // reconnect on every render that produced a new function identity, which for
    // a default parameter is never - until somebody passes an inline lambda, and
    // then it is every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  /**
   * ⛔ Ctrl+K / Cmd+K, ON THE DOCUMENT, AND `preventDefault` IS LOAD-BEARING.
   * Cmd+K is a browser shortcut on some platforms and Ctrl+K focuses the address
   * bar on others, so without it the palette opens BEHIND the browser taking the
   * key - which reads as the shortcut not working.
   *
   * ⚠️ AND IT LISTENS ON `document`, not on a focused element, because the point
   * of the shortcut is that it works wherever you are - including inside the
   * composer, which is where an agent's cursor spends the day.
   */
  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault()
        setPaletteOpen(true)
      }
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [])

  const { banner, listStale, retryLine } = presentConnection(state)

  return (
    <div className="inbox-shell">
      {/*
        ⛔ THE PALETTE'S ACTIONS ARE THE HEADER'S CONTROLS, NOT A SECOND
        IMPLEMENTATION. `onAction` asks the header to open the menu it already
        owns, so snooze and assign have ONE implementation, one confirm rule and
        one failure line. A palette that wrote directly would be a second writer
        for the same fact - the shape `assignable-users.ts` exists to prevent one
        layer down.
      */}
      <CommandPalette
        open={paletteOpen}
        onClose={() => setPaletteOpen(false)}
        conversationId={conversationId}
        onOpenConversation={(id) => onOpen?.(id)}
        onAction={setHeaderMenu}
      />
      {banner !== null && (
        /**
         * `role="status"` and `aria-live="polite"`, not `alert`. The connection
         * dropping is not an emergency and does not interrupt what an agent is
         * doing; it is a condition they need to know about at the next natural
         * pause. `alert` would preempt a screen reader mid-sentence.
         */
        <div className="inbox-shell__banner" role="status" aria-live="polite">
          {banner}
          {/*
            ⛔ A PROBLEM WITH NO PROSPECT IS WHAT MAKES PEOPLE RELOAD, and a
            reload during a backoff is the one action that makes it worse. The
            figure is the socket's own `nextAttemptInMs` - no timer was added.

            ⚠️ IT DOES NOT TICK. The number updates when the socket's state
            does, so it is the delay that was CHOSEN rather than a live
            countdown. A ticking one would need an interval whose only job is
            re-rendering the shell every second, forever, for a line nobody is
            watching.
          */}
          {retryLine !== null && (
            <span className="inbox-shell__banner-retry" data-testid="banner-retry">
              {retryLine}
            </span>
          )}
        </div>
      )}

      {/*
        ⛔ THE LIST'S DATA COMES THROUGH THE QUERY REGISTRY, so `refetchAll()`
        on reconnect re-runs it. That is the whole point of the registry, and
        it is what makes the socket a hint rather than a transport: a frame
        arrives, everything materialised refetches, and the database answers.

        ⚠️ `now` IS CREATED ONCE PER RENDER rather than inside each row. Calling
        `new Date()` per item would give rows in one list slightly different
        "now"s, which is invisible until two rows a second apart render as the
        same relative time or the reverse.
      */}
      <div className="inbox-shell__panes">
        {/*
          ⛔ THE LIST LOOKS STALE WHEN IT IS STALE. The banner alone was not
          enough: above the threshold every row still rendered crisp, with a
          green presence dot and a fresh-looking timestamp, so an agent who
          scrolled past the banner - or came back to a tab they had left open -
          read an out-of-date list as current.

          ⚠️ THE CLASS IS THE WHOLE MECHANISM. Nothing here refetches, retries or
          hides a row: the data is the last true answer we had and it is still
          the best one on screen. Dimming says "this may be out of date", which
          is exactly what is true, and REMOVING it would say something false.
        */}
        <div className={`inbox-shell__list${listStale ? ' inbox-shell__list--stale' : ''}`}>
          {/*
            ┌─ ⛔ THE PALETTE HAD NO VISIBLE ENTRY POINT ────────────────────────┐
            │ Amir's inspection: *"Ctrl+K only, which nobody discovers."* A      │
            │ keyboard shortcut is a shortcut TO something; it was the only way  │
            │ in, so item 21's whole surface was invisible to anyone who had not │
            │ been told about it.                                                │
            │                                                                    │
            │ ⚠️ IT SITS ABOVE THE LIST, WHERE A SEARCH FIELD WOULD BE, and says │
            │ the shortcut on itself. Discovering the control and learning the   │
            │ shortcut are then the same act, rather than the second requiring   │
            │ documentation nobody reads.                                        │
            │                                                                    │
            │ ⚠️ AND IT IS A BUTTON, NOT AN INPUT. It looks like a search box    │
            │ because that is what makes it findable, but typing into a field    │
            │ that then re-renders as an overlay with its own field loses the    │
            │ first keystroke - the search page's bug in a different costume.    │
            └────────────────────────────────────────────────────────────────────┘
          */}
          <button
            type="button"
            className="inbox-shell__palette-open"
            data-testid="palette-open"
            onClick={() => setPaletteOpen(true)}
          >
            <span>Search Conversations And People</span>
            {/* ⚠️ THE PLATFORM'S OWN WORD. "Ctrl" on a Mac is a different key. */}
            <kbd className="inbox-shell__kbd">{shortcutHint()}</kbd>
          </button>

          {/* ⚠️ THE SHELL ALREADY HELD `conversationId` - it is what drives the
              thread and the panel. The list simply never received it, which is
              why the gap was four small pieces rather than a feature. */}
          <ConversationList
            rows={rows}
            now={new Date()}
            error={error}
            selectedId={conversationId}
            onOpen={onOpen}
            more={more}
            onLoadMore={loadMore}
          />
        </div>

        <div
          className="inbox-shell__thread"
          /**
           * ⚠️ WHICH CONVERSATION THIS PANE IS SHOWING, READABLE FROM THE DOM.
           * Added 2026-09-11 with `/inbox/contacts/<n>`, because the question
           * that URL raises is *which* thread opened - and the list's selected
           * row cannot answer it: a contact's newest thread is frequently not on
           * the first page of fifty, so "no row is marked current" is the
           * ordinary case rather than a failure.
           */
          data-conversation-id={conversationId ?? undefined}
        >
          {/*
            ⛔ ABOVE THE THREAD AND OUTSIDE THE no-conversation BRANCH, so it
            renders in every state. It draws an empty bar when nothing is open -
            same reasoning as the panel pane existing then: unmounting it would
            move the message list by the header's height on every click.

            ⚠️ IT TAKES `panel`, WHICH IS ALREADY FETCHED. The header's four
            facts are the panel's identity row, so a second endpoint would be a
            second query answering one question - and the two could disagree on
            screen at the same moment.
          */}
          <ThreadHeader
            panel={panel}
            conversationId={conversationId}
            /* Status, assignment and snooze all change the LIST as well as this
               conversation, which is why it is `refetchAll` and not the panel
               alone: a closed conversation leaves the default view. */
            onChanged={async () => {
              await refetchAll()
            }}
            requestedMenu={headerMenu}
            onMenuOpened={() => setHeaderMenu(null)}
          />

          {conversationId === null ? (
            /**
             * ⛔ A FOURTH EMPTY STATE, AND IT IS NOT ONE OF THE THREE `ThreadView`
             * OWNS. Loading, empty and error all mean *a conversation is open and
             * here is what we know about it*. This one means **no conversation is
             * open**, and rendering it as `ThreadView`'s empty state would tell an
             * agent staring at an unselected pane that the conversation they have
             * not opened has no messages.
             *
             * ⚠️ Same distinction the list draws between `null` and `[]`, one
             * level up. The count of empty states this phase has had to separate
             * is now five, and every one of them was found by asking what a wrong
             * answer would look like rather than by anything failing.
             */
            <p className="inbox-shell__no-thread" data-testid="thread-none">
              Select a conversation
            </p>
          ) : (
            <ThreadView
              rows={threadRows}
              pinned={threadPinned}
              error={threadError}
              highlightMessageId={highlightMessageId}
              more={threadMore}
              onLoadMore={loadMoreThread}
              currentUserId={currentUserId}
              /*
                ⚠️ EVERY ONE REFETCHES THROUGH THE STORE rather than patching a
                row in place. A note that is edited, deleted or pinned changes
                the thread AND the list's preview, and `refetchAll` is the path a
                reconnect already takes - so this is exercised rather than being
                a second update mechanism nobody else uses.
              */
              onEditNote={async (id, body) => {
                await editNoteApi(id, body)
                await refetchAll()
              }}
              onDeleteNote={async (id) => {
                await deleteNoteApi(id)
                await refetchAll()
              }}
              onPinNote={async (id, pinned) => {
                await pinNoteApi(id, pinned)
                await refetchAll()
              }}
            />
          )}

          {/*
            ⛔ THE COMPOSER LIVES BELOW THE THREAD AND RENDERS EVEN WITH NOTHING
            OPEN. It draws its own idle line, which is a different sentence from
            a disabled box - "nothing is open" against "you cannot send here".

            ⚠️ `lastErrorCode` IS THE NEWEST OUTBOUND ROW'S, which is the only
            signal available today: the window state is not on the wire. The
            composer is therefore REACTIVE - it learns a condition from a
            failure - and the proactive read is a thread-endpoint change that is
            not in this piece.
          */}
          <Composer
            conversationId={conversationId}
            /**
             * ⛔ THE THREAD'S LOAD FAILURE, NOT THE PANEL'S. `panelError` means
             * we could not read the identity card - annoying, and no reason to
             * refuse a reply to a conversation whose messages are on screen.
             * `threadError` means we could not read the conversation itself.
             */
            threadUnavailable={threadError !== null}
            lastErrorCode={
              threadRows?.find((m) => m.direction === 'outbound')?.error_code ?? null
            }
            onSend={async (text, tab) => {
              if (conversationId === null) return
              await postComposed(
                conversationId,
                tab === 'note' ? 'notes' : 'messages',
                text,
                /**
                 * ⛔ MINTED HERE, AT COMMIT, AND NOWHERE ELSE. Per composer-open
                 * would collapse a deliberate second send into a retry; inside
                 * the fetch would make every retry a new message. Only this
                 * point knows when an intent began.
                 */
                crypto.randomUUID(),
              )
              /**
               * The write does not go through `subscribe()` - re-running it on
               * reconnect would send again - so the refetch is explicit.
               * `refetchAll` re-runs the thread AND the list, because a new
               * message changes both.
               */
              await refetchAll()
            }}
          />
        </div>

        {/*
          ⛔ THE PANEL PANE EXISTS EVEN WITH NOTHING OPEN, and `RightPanel`
          renders its own no-conversation state rather than being unmounted.

          Unmounting it would make the two-pane and three-pane layouts different
          widths, so the thread would jump sideways on every click - which reads
          as a rendering bug and is the kind of thing only a browser can see.
        */}
        <div className="inbox-shell__panel">
          <RightPanel panel={panel} error={panelError} />
        </div>
      </div>
    </div>
  )
}
